import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Plus, Trash2, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { api, type CategoryAttributeDefinition } from "@/api";
import { categoryKeys } from "@/api/hooks/use-categories";
import { Select } from "@/components/ui/select";
import { attributeKeyFromLabel, type CategoryTree, type CategoryTreeRow, subtreeIds, validateAttributeDraft } from "@/lib/category-tree";
const TYPES = ["text", "number", "boolean", "select"] as const;
type DraftAttr = {
  key: string;
  /** Ключ задан руками — не пересчитываем из названия. */
  keyTouched: boolean;
  label: string;
  type: CategoryAttributeDefinition["type"];
  required: boolean;
  options: string;
};
const toDraft = (a: CategoryAttributeDefinition): DraftAttr => ({
  key: a.key,
  keyTouched: true,
  label: a.label,
  type: a.type,
  required: !!a.required,
  options: (a.options ?? []).join(", ")
});
const fromDraft = (d: DraftAttr): CategoryAttributeDefinition => {
  const out: CategoryAttributeDefinition = {
    key: d.key.trim(),
    label: d.label.trim(),
    type: d.type
  };
  if (d.required) out.required = true;
  if (d.type === "select") out.options = d.options.split(",").map(o => o.trim()).filter(Boolean);
  return out;
};

/** Цепочка «узел → корень»: своя схема каждого узла (без наследования). */
function useSchemaChain(row, tree) {
  const $ = _c(5);
  let chain;
  if ($[0] !== row || $[1] !== tree) {
    chain = [];
    for (let cur = row; cur && chain.length < 10;) {
      chain.push(cur);
      cur = cur.category.parentId ? tree.byId.get(cur.category.parentId) : undefined;
    }
    $[0] = row;
    $[1] = tree;
    $[2] = chain;
  } else {
    chain = $[2];
  }
  let t0;
  if ($[3] !== chain) {
    t0 = {
      queryKey: [...categoryKeys.all, "schema", ...chain.map(_temp)],
      queryFn: async () => Promise.all(chain.map(_temp2))
    };
    $[3] = chain;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return useQuery(t0);
}

/**
 * Характеристики узла: своя схема или унаследованная от ближайшего предка
 * (так же берёт форма товара на шлюзе). Схема, заданная на узле второго
 * уровня («Женщинам / Обувь»), действует у всех его видов, пока у вида нет
 * своей. Обязательные характеристики продавец не может пропустить.
 */
async function _temp2(node) {
  return {
    node,
    attributes: (await api.categories.attributeSchema(node.category.id)).attributes
  };
}
function _temp(c) {
  return c.category.id;
}
export function CategorySchemaEditor({
  row,
  tree,
  canEdit
}: {
  row: CategoryTreeRow;
  tree: CategoryTree;
  canEdit: boolean;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const query = useSchemaChain(row, tree);
  const [draft, setDraft] = useState<DraftAttr[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  if (query.isLoading) return <p className="text-sm text-muted-foreground">{t("categories.tree.schemaLoading")}</p>;
  if (query.error) return <p className="text-sm text-destructive">{(query.error as Error).message}</p>;
  const chain = query.data ?? [];
  const own = chain[0]?.attributes ?? [];
  const effective = chain.find(c => c.attributes.length > 0);
  const inherited = effective && effective.node.category.id !== row.category.id ? effective : null;
  // Узлов ниже в ветке — у кого нет своей схемы, действует эта.
  const descendants = subtreeIds(tree.active.map(r => r.category), row.category.id).size - 1;
  const startEdit = () => {
    setError(null);
    const base = own.length ? own : inherited?.attributes ?? [];
    setDraft(base.map(toDraft));
  };
  const save = async () => {
    if (!draft) return;
    const attributes = draft.map(fromDraft);
    const problem = validateAttributeDraft(attributes);
    if (problem) {
      setError(t(`categories.schemaEditor.errors.${problem.code}`, {
        key: problem.key
      }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.categories.updateAttributeSchema(row.category.id, attributes);
      await queryClient.invalidateQueries({
        queryKey: [...categoryKeys.all, "schema"]
      });
      setDraft(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const patch = (i: number, next: Partial<DraftAttr>) => setDraft(prev => prev ? prev.map((d, j) => {
    if (j !== i) return d;
    const merged = {
      ...d,
      ...next
    };
    if (next.label !== undefined && !d.keyTouched) merged.key = attributeKeyFromLabel(next.label);
    return merged;
  }) : prev);
  const swap = (i_0: number, dir: -1 | 1) => setDraft(prev_0 => {
    if (!prev_0) return prev_0;
    const j_0 = i_0 + dir;
    if (j_0 < 0 || j_0 >= prev_0.length) return prev_0;
    const next_0 = [...prev_0];
    [next_0[i_0], next_0[j_0]] = [next_0[j_0] as DraftAttr, next_0[i_0] as DraftAttr];
    return next_0;
  });
  if (draft) {
    return <div className="space-y-3" data-testid={`category-schema-editor-${row.category.id}`}>
				{!own.length && <p className="text-xs text-muted-foreground">
						{inherited ? t("categories.schemaEditor.fromInherited", {
          name: inherited.node.path.join(" / ")
        }) : t("categories.schemaEditor.fromEmpty")}
					</p>}
				<div className="space-y-2">
					{draft.map((d_0, i_1) => <div key={i_1} className="flex flex-wrap items-center gap-2 rounded-md border bg-background p-2" data-testid="schema-attr-row">
							<Input value={d_0.label} placeholder={t("categories.schemaEditor.label")} aria-label={t("categories.schemaEditor.label")} onChange={e_0 => patch(i_1, {
            label: e_0.target.value
          })} className="h-8 w-56" data-testid="schema-attr-label" />
							<Input value={d_0.key} placeholder={t("categories.schemaEditor.key")} aria-label={t("categories.schemaEditor.key")} title={t("categories.schemaEditor.keyHint")} onChange={e_1 => patch(i_1, {
            key: e_1.target.value,
            keyTouched: true
          })} className="h-8 w-40 font-mono text-xs" data-testid="schema-attr-key" />
							<Select value={d_0.type} aria-label={t("categories.schemaEditor.type")} onChange={e_2 => patch(i_1, {
            type: e_2.target.value as DraftAttr["type"]
          })} className="h-8 w-36" data-testid="schema-attr-type">
								{TYPES.map(type => <option key={type} value={type}>
										{t(`categories.schemaEditor.types.${type}`)}
									</option>)}
							</Select>
							{d_0.type === "select" && <Input value={d_0.options} placeholder={t("categories.schemaEditor.options")} aria-label={t("categories.schemaEditor.options")} onChange={e_3 => patch(i_1, {
            options: e_3.target.value
          })} className="h-8 w-56" data-testid="schema-attr-options" />}
							<label className="flex items-center gap-1.5 text-sm">
								<input type="checkbox" checked={d_0.required} onChange={e_4 => patch(i_1, {
              required: e_4.target.checked
            })} data-testid="schema-attr-required" />
								{t("categories.tree.schemaRequired")}
							</label>
							<div className="ml-auto flex gap-1">
								<Button size="sm" variant="ghost" onClick={() => swap(i_1, -1)} disabled={i_1 === 0} aria-label={t("categories.moveUp")}>
									<ArrowUp className="size-4" />
								</Button>
								<Button size="sm" variant="ghost" onClick={() => swap(i_1, 1)} disabled={i_1 === draft.length - 1} aria-label={t("categories.moveDown")}>
									<ArrowDown className="size-4" />
								</Button>
								<Button size="sm" variant="ghost" onClick={() => setDraft(draft.filter((_, j_1) => j_1 !== i_1))} aria-label={t("categories.schemaEditor.removeAttr")}>
									<Trash2 className="size-4" />
								</Button>
							</div>
						</div>)}
					{draft.length === 0 && <p className="text-sm text-muted-foreground">{t("categories.schemaEditor.noAttrs")}</p>}
				</div>
				{error && <p className="text-sm text-destructive" data-testid="schema-error">
						{error}
					</p>}
				<div className="flex flex-wrap gap-2">
					<Button size="sm" variant="outline" onClick={() => setDraft([...draft, {
          key: "",
          keyTouched: false,
          label: "",
          type: "text",
          required: false,
          options: ""
        }])} data-testid="schema-add-attr">
						<Plus className="size-4" /> {t("categories.schemaEditor.addAttr")}
					</Button>
					<Button size="sm" onClick={() => void save()} disabled={saving} data-testid="schema-save">
						{t("categories.save")}
					</Button>
					<Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
						<X className="size-4" /> {t("categories.cancel")}
					</Button>
				</div>
				<p className="text-xs text-muted-foreground">{t("categories.schemaEditor.saveHint")}</p>
			</div>;
  }

  // «Сбросить к наследованию»: своя схема удаляется, форма товара снова берёт
  // схему ближайшего предка (так же, как шлюз при пустой схеме узла).
  const resetToInherited = async () => {
    setResetting(true);
    setError(null);
    try {
      await api.categories.resetAttributeSchema(row.category.id);
      await queryClient.invalidateQueries({
        queryKey: [...categoryKeys.all, "schema"]
      });
    } catch (e_5) {
      setError((e_5 as Error).message);
    } finally {
      setResetting(false);
    }
  };
  const parentHasSchema = chain.slice(1).some(c_0 => c_0.attributes.length > 0);
  const shown = own.length ? own : inherited?.attributes ?? [];
  return <div className="space-y-2" data-testid={`category-schema-${row.category.id}`}>
			{inherited ? <p className="text-xs text-muted-foreground" data-testid="schema-inherited-from" data-from={inherited.node.category.id}>
					{t("categories.tree.schemaInherited", {
        name: inherited.node.path.join(" / ")
      })}
				</p> : own.length ? <p className="text-xs text-muted-foreground">
					{t("categories.schemaEditor.ownHint", {
        count: descendants
      })}
				</p> : null}
			{shown.length > 0 ? <div className="flex flex-wrap gap-1.5">
					{shown.map(a => <Badge key={a.key} variant={a.required ? "default" : "outline"} data-testid="schema-attr" data-key={a.key} data-required={a.required ? "true" : "false"}>
							{a.label}
							{a.required && <span className="ml-1 opacity-80">· {t("categories.tree.schemaRequired")}</span>}
						</Badge>)}
				</div> : <p className="text-sm text-muted-foreground">{t("categories.tree.schemaEmpty")}</p>}
			{error && <p className="text-sm text-destructive" data-testid="schema-error">
					{error}
				</p>}
			{canEdit && !row.archived && <div className="flex flex-wrap gap-2">
					<Button size="sm" variant="outline" onClick={startEdit} data-testid="schema-edit">
						{own.length ? t("categories.schemaEditor.edit") : t("categories.schemaEditor.createOwn")}
					</Button>
					{own.length > 0 && <Button size="sm" variant="ghost" onClick={() => void resetToInherited()} disabled={resetting} title={parentHasSchema ? t("categories.schemaEditor.resetHint") : t("categories.schemaEditor.resetHintNoParent")} data-testid="schema-reset">
							{t("categories.schemaEditor.reset")}
						</Button>}
				</div>}
		</div>;
}